import { Billboard } from '@react-three/drei'
import { useMemo } from 'react'
import { suspend } from 'suspend-react'
import * as THREE from 'three'

import AsyncBoundary from '@components/AsyncBoundary'
import { transformImageUrl } from '@components/productImage/data'
import { useRegisterForAnimation } from '@components/storeMap/floor/3d/AnimationContext'
import { loadTexture, useBobbing } from '@components/storeMap/floor/3d/util'
import { useLayoutWithMapOrigin } from '@components/storeMap/utils'
import { useProductFinderContext } from '@routes/ProductFinder/Context'
import { ProductWrapper, usePlacementsByGtin, useProductSearchByGTIN } from '@state/product.ts'

const PIN_WIDTH = 1.5
const PIN_HEIGHT = PIN_WIDTH * (38 / 34) // Maintain SVG aspect ratio (34x38)

import PinIcon from '../../../components/storeMap/assets/pinProd.svg?no-inline'

function WrappedProductMarkers() {
  return (
    <AsyncBoundary>
      <ProductMarkers />
    </AsyncBoundary>
  )
}

function ProductMarkers() {
  useRegisterForAnimation()

  const { data: layout } = useLayoutWithMapOrigin()
  const { selectedGTIN } = useProductFinderContext()
  const shelfIds = usePlacementsByGtin(selectedGTIN)
  const { data: productsByGtin } = useProductSearchByGTIN(selectedGTIN)

  const coordinates = useMemo(() => {
    if (shelfIds.length === 0 || !layout) return []

    return layout.shelves
      .filter(shelf => shelfIds.includes(shelf.id))
      .map((shelf) => ({ x: shelf.center.x, y: shelf.center.y }))
  }, [shelfIds, layout])

  return coordinates.map(position => {
    return <ProductMarker key={JSON.stringify(position)} product={productsByGtin?.items[0]} position={position} />
  })
}

interface ProductMarkerProps {
  product?: ProductWrapper
  position: {x: number; y: number}
}

function ProductMarker({ product, position }: ProductMarkerProps) {
  const bobbingRef = useBobbing()

  const myPosition = [position.x, 2.2, position.y] as const
  const productImageUrl = product?.imageUrl

  const pinTexture = suspend(() => loadTexture({ imageUrl: PinIcon, size: 150 }), [PinIcon]) as THREE.Texture

  let productTexture = undefined
  if (productImageUrl) {
    productTexture = suspend(() => loadTexture({
      imageUrl: transformImageUrl(productImageUrl, 'prodMarkerMap'),
      size: 100,
    }),
    [productImageUrl]) as THREE.Texture
  }

  if (!productTexture) return null

  return (
    <group ref={bobbingRef} position={myPosition}>
      <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
        <mesh position={[0, PIN_HEIGHT, 0]}>
          <planeGeometry args={[PIN_WIDTH, PIN_HEIGHT]} />
          <meshBasicMaterial map={pinTexture} transparent side={THREE.DoubleSide} />
        </mesh>

        <mesh position={[0, PIN_HEIGHT + 0.15, 0.1]}>
          <planeGeometry args={[PIN_WIDTH * 0.5, PIN_HEIGHT * 0.5]} />
          <meshBasicMaterial map={productTexture} transparent side={THREE.DoubleSide} />
        </mesh>
      </Billboard>

      <mesh position={[0, PIN_HEIGHT, 0]} castShadow>
        <sphereGeometry args={[0.4, 32, 32]} />
        <meshBasicMaterial colorWrite={false} depthWrite={false} />
      </mesh>
    </group>
  )
}

export default WrappedProductMarkers